import { FC, useEffect, useState, useMemo, ChangeEvent } from 'react';
import { Column, useTable } from 'react-table';
import HelperService from '../../services/helper'
import useNotyf from '../../context/useNotify';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrashAlt, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import ReactPaginate from 'react-paginate';
import { Meta, QueryParams } from '../../models/misc';
import { StyledInstructions, StyledPagination, StyledTable } from './Styles';
import ReactModal from '../common/ReactModal';
import TeamService from "../../services/team"
import AdminTeamService from "../../services/admin/adminTeam"
import { CreateTeamDTO, Team, TeamSingle, UpdateTeamDTO, AdminAddPlayerDTO } from '../../models/team';
import { formatLiveLinkUrl } from '../../utils/formatString';
import instructions from "../../content-options/admin/instructions"
import ConfirmationModalContent from '../common/ConfirmationModalContent';

const TeamTable: FC = () => {
  const notyf = useNotyf()

  // Data
  const [teams, setTeams] = useState<Team[]>([]);
  
  // API
    const fetchTeams = async (page: number = 1) => {
    try {
      const filters: QueryParams = { 
        page: page,
        pageSize: meta.pageSize 
      };
      const data = await TeamService.getTeams(filters);
      setTeams(data.data);
      setMeta(data.meta);
    } catch (err) {
      console.log("Error fetching teams: ", err);
      HelperService.showNotfyErr(notyf, err, "Error fetching teams.");
    }
    };
  
    const updateTeam = async (id: string, updateTeam: TeamSingle) => {
        try {
            const updateDto: UpdateTeamDTO = {
                name: updateTeam.name, 
                status: updateTeam.status,
                callTimes: updateTeam.callTimes,
                image: updateTeam.image,
                liveLink: updateTeam.liveLink,
                continent: updateTeam.continent,
                console: updateTeam.console,
                dates: updateTeam.dates,
                times: updateTeam.times,
                registered: updateTeam.registered,
              };
                        
        const updated = await AdminTeamService.updateTeam(id, updateDto);
        notyf.success("Team updated.")
        fetchTeams(meta.page)
      } catch (err) {
        console.log("Error updating team: ", err);
        HelperService.showNotfyErr(notyf, err, "Error updating team.");
      }
    };
  
    const getTeam = async (id: string) => {
      try {
        const team = await TeamService.getTeamById(id);
        return team
      } catch (err) {
        console.log("Error fetching team: ", err);
        HelperService.showNotfyErr(notyf, err, "Error fetching team.");
      }
    };

    const createTeam = async (dto: CreateTeamDTO) => {
        try {
          const team = await AdminTeamService.createTeam(dto);
          notyf.success("Team created.")
          fetchTeams(meta.page)
        } catch (err) {
          console.log("Error creating team: ", err);
          HelperService.showNotfyErr(notyf, err, "Error creating team.");
        }
    };
  
    const deleteTeam = async (id: string) => {
      try {
        await AdminTeamService.deleteTeam(id);
        notyf.success("Team deleted.")
        fetchTeams(meta.page)
      } catch (err) {
        console.log("Error deleting team: ", err);
        HelperService.showNotfyErr(notyf, err, "Error deleting team.");
      }
    };
  
    const addPlayer = async (teamId: string, AdminAddPlayerDTO: AdminAddPlayerDTO) => {
      try {
        await AdminTeamService.addPlayer(teamId, AdminAddPlayerDTO)
        notyf.success("Player added.")
        fetchTeams(meta.page)
      } catch (err) {
        console.log("Error adding player: ", err);
        HelperService.showNotfyErr(notyf, err, "Error adding player.");
      }
    }
      
    // Methods
    const handleClickUpdate = async (id: string) => {
        const fetchedTeam = await getTeam(id)
        if (fetchedTeam) {
            setTeamToUpdate(fetchedTeam)
        }
      setShowUpdateModal(true);
    };

    const handleClickCreate = () => {
      setShowCreateModal(true);
    };

    const handleClickAddPlayer = (team: Team) => {
      setTeamToAddPlayer(team);
      setShowAddPlayerModal(true);
    };
  
  // Hooks
  useEffect(() => {
    fetchTeams()
  }, []);

  // Table Data
  const columns: Column<Team>[] = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({ row }) => (
          <div className="d-flex align-items-center">
            <img
              src={row.original.image}
              alt={row.original.name}
              className="img-fluid me-2" // Bootstrap classes for responsive image and margin
              style={{ maxWidth: '30px', maxHeight: '30px', borderRadius: '50%' }} // Adjust size and make it circular
            />
            {row.original.name}
          </div>
        ),
      },
      {
        Header: 'Live Link',
        accessor: 'liveLink',
        Cell: ({ row }) => {
          const formattedLink = formatLiveLinkUrl(row.original.liveLink);
          return (
            <a href={formattedLink.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-reset">
              {formattedLink.displayName}
            </a>
          );
        },
      },
          {
            Header: 'Console',
            accessor: 'console',
          },
          {
            Header: 'Manager',
            accessor: 'manager',
      },
      {
        Header: 'Players',
        accessor: team => team.roster.length,
        id: 'players',
      },      
      {
        Header: 'Actions',
        accessor: (team: Team) => team,
        Cell: ({ cell: { value: team } }: { cell: { value: Team } }) => (
          <div className="actions d-flex">
            <button className='btn btn-warning btn-sm me-2' onClick={() => handleClickUpdate(team._id)}>
            <FontAwesomeIcon icon={faEdit} /> Update
            </button>
            <button className='btn btn-danger btn-sm me-2' onClick={() => handleShowDeleteModal(team)}>
            <FontAwesomeIcon icon={faTrashAlt} /> Delete
            </button>
            <button className='btn btn-primary btn-sm' onClick={() => handleClickAddPlayer(team)}>
              <FontAwesomeIcon icon={faUserPlus} /> Add Player
            </button>
          </div>
        ),
        className: 'actions',
        width: 150
      },
    ],
    [teams]
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data: teams });

  // Pagination
  const [meta, setMeta] = useState<Meta>({
    totalPages: 0,
    page: 1,
    pageSize: 25
  });
    
    // Modals
  
    const closeModals = () => {
        setShowUpdateModal(false);
        setShowCreateModal(false);
        setTeamToUpdate(null);
        setTeamToCreate({ name: "", username: "" });
        setShowDeleteModal(false);
        setTeamToDelete(null);
        setShowAddPlayerModal(false);
        setTeamToAddPlayer(null);
        setPlayerToAdd({ username: "", seasonsRemaining: 5 });
    };

    // Modal - Update
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [teamToUpdate, setTeamToUpdate] = useState<TeamSingle | null>(null);

    const handleUpdateInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (teamToUpdate) {
        setTeamToUpdate({ ...teamToUpdate, [name]: value });
      }
    };
    
    const handleUpdateSubmit = async (e: any) => {
        e.preventDefault();
        if (teamToUpdate) {
        await updateTeam(teamToUpdate._id, teamToUpdate);
        }
        closeModals();
        fetchTeams(1)
        }
    
    // Modal - Create
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [teamToCreate, setTeamToCreate] = useState<CreateTeamDTO>({name: "", username: ""});

    const handleCreateInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setTeamToCreate({ ...teamToCreate, [name]: value });
    };

    const handleCreateSubmit = async (e: any) => {
        e.preventDefault();
        if (teamToCreate) {
            await createTeam(teamToCreate);
        }
        closeModals();
        fetchTeams(1)
    }
  
  // Modal - Delete
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);

  const handleShowDeleteModal = (team: Team) => {
    setTeamToDelete(team);
    setShowDeleteModal(true);
  }

  const handleClickDelete = async () => {
    if (!teamToDelete) {
      return;
    }
    await deleteTeam(teamToDelete._id);
    closeModals();
  }

  // Modal - Add Player
  const [showAddPlayerModal, setShowAddPlayerModal] = useState<boolean>(false);
  const [teamToAddPlayer, setTeamToAddPlayer] = useState<Team | null>(null);
  const [playerToAdd, setPlayerToAdd] = useState<AdminAddPlayerDTO>({ username: "", seasonsRemaining: 5 });

  const handleAddPlayerInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPlayerToAdd({ ...playerToAdd, [name]: value });
  };

  const handleAddPlayerSubmit = async (e: any) => {
    e.preventDefault();
    if (teamToAddPlayer && playerToAdd) {
      await addPlayer(teamToAddPlayer._id, playerToAdd);
    }
    closeModals();
  };


  return (
      <>
          
      <div className='d-flex flex-column mb-3'>
        {/* Fetch Data Button */}
        {/* <button 
            className='btn btn-bark mb-2' 
            onClick={() => fetchTeams()}
            style={{ color: teams.length > 0 ? '' : 'white' }}
          >
            Fetch Data
        </button> */}
        <StyledInstructions>
          { instructions.table.team }
        </StyledInstructions>
        <div className='ms-auto mr-3 my-2'>          
          <button className='btn btn-primary' onClick={handleClickCreate}>Create Team</button>
        </div>
        <div className='table-responsive'>
          <StyledTable {...getTableProps()}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </div>
      </div>
      <div className='d-flex justify-content-end'>
        {teams.length ?
            <StyledPagination>
              <ReactPaginate
                previousLabel={"Previous"}
                nextLabel={"Next"}
                pageCount={meta.totalPages}
                onPageChange={({ selected }) => {
                    fetchTeams(selected + 1);
                  }}
                containerClassName={"pagination"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                nextClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
              />
            </StyledPagination>
        : null}
          </div>
          
      {/* Update Modal */}
      <ReactModal 
  modalOpen={showUpdateModal} 
  closeModal={() => closeModals()}
  component={
    <>
      <form onSubmit={handleUpdateSubmit} className='m-5'>
        <div className="mb-3">
          <strong>Team Information:</strong><br/>
          Name: {teamToUpdate?.name}<br/>
          Continent: {teamToUpdate?.continent}<br/>
          Live Link: {teamToUpdate?.liveLink}<br/>
          Console: {teamToUpdate?.console}<br/>          Console: {teamToUpdate?.console}<br/>
          Image: {teamToUpdate?.image}<br/>
        </div>

            <div className="mb-3">
              <label htmlFor="teamName" className="form-label">Name</label>
              <input 
                  type="text" 
                  id="teamName" 
                  name="name" 
                  className="form-control" 
                  value={teamToUpdate?.name || ""} 
                  onChange={handleUpdateInputChange} 
              />
            </div>
            <div className="mb-3">
            <label htmlFor="liveLink" className="form-label">Live Link</label>
            <input 
                type="text"
                id="liveLink" 
                name="liveLink" 
                className="form-control" 
                value={teamToUpdate?.liveLink || ""} 
                onChange={handleUpdateInputChange} 
            />
            </div>
            <div className="mb-3">
            <label htmlFor="image" className="form-label">Image</label>
            <input 
                type="text" 
                id="image" 
                name="image" 
                className="form-control" 
                value={teamToUpdate?.image || ""} 
                onChange={handleUpdateInputChange} 
            />
        </div>

        <button type="submit" className="btn btn-primary">Update</button>
      </form>
    </>
  }
          />
          
{/* Create Modal */}
<ReactModal 
  modalOpen={showCreateModal} 
  closeModal={() => closeModals()}
  component={
    <>
      <form onSubmit={handleCreateSubmit} className='m-5'>
        <div className="mb-3">
          <label htmlFor="leagueName" className="form-label">Name</label>
          <input 
            type="text" 
            id="leagueName" 
            name="name" 
            className="form-control" 
            value={teamToCreate?.name || ""} 
            onChange={handleCreateInputChange} 
          />
        </div>
        <div className="mb-3">
          <label htmlFor="username" className="form-label">Manager</label>
          <input 
            type="text" 
            id="username" 
            name="username" 
            className="form-control" 
            value={teamToCreate?.username || ""} 
            onChange={handleCreateInputChange} 
          />
        </div>
        <button type="submit" className="btn btn-primary">Create</button>
      </form>
    </>
  }
      />
      
      {/* Delete Modal */}
      <ReactModal
        modalOpen={showDeleteModal}
        component={<ConfirmationModalContent
          onConfirm={handleClickDelete}
          onCancel={() => closeModals()}
          message={`Are you sure you want to delete ${teamToDelete?.name}?`}
        />}
        closeModal={() => closeModals()}
      />

  {/* Add Player Modal */}
  <ReactModal
    modalOpen={showAddPlayerModal}
    closeModal={() => closeModals()}
    component={
      <>
        <form onSubmit={handleAddPlayerSubmit} className='m-5'>
          <h3 className='mb-4'>Add Player to {teamToAddPlayer?.name}</h3>
          <div className="mb-3">
            <label htmlFor="username" className="form-label">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              className="form-control"
              value={playerToAdd.username}
              onChange={handleAddPlayerInputChange}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="seasonsRemaining" className="form-label">Seasons Remaining</label>
            <input
              type="number"
              id="seasonsRemaining"
              name="seasonsRemaining"
              className="form-control"
              value={playerToAdd.seasonsRemaining}
              onChange={handleAddPlayerInputChange}
              min="1"
              required
            />
          </div>
          <div className="d-flex justify-content-end">
            <button type="button" className="btn btn-secondary me-2" onClick={() => closeModals()}>Cancel</button>
            <button type="submit" className="btn btn-primary">Add Player</button>
          </div>
        </form>
      </>
    }
  />

    </>
  );
};

export default TeamTable;
